import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveTwin } from '../utils/twinStorage';
import './ConnectTwin.css';

const API = process.env.REACT_APP_AUTH_API || process.env.REACT_APP_API_URL || 'https://0g75gzq525.execute-api.us-east-1.amazonaws.com/dev';

interface AWSWorkspace {
  workspaceId: string;
  workspaceName: string;
  s3Location: string;
  role: string;
  creationDateTime: string;
  updateDateTime: string;
  description?: string;
  region?: string;
}

interface ConnectedTwinData {
  workspaceId: string;
  twinMakerWsId: string;
  company: string;
  factoryType: string;
  machineCount: number;
  sensorCount: number;
  sceneCount: number;
  status: string;
}

const ConnectTwin: React.FC = () => {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<AWSWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkspace, setSelectedWorkspace] = useState<AWSWorkspace | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [awsRegion, setAwsRegion] = useState('us-east-1');
  const [awsAccessKey, setAwsAccessKey] = useState('');
  const [awsSecretKey, setAwsSecretKey] = useState('');
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [connectedTwin, setConnectedTwin] = useState<ConnectedTwinData | null>(null);

  // Fetch AWS workspaces
  const fetchWorkspaces = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/aws/list-workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region: awsRegion,
          accessKey: awsAccessKey,
          secretKey: awsSecretKey,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch AWS workspaces');
      }

      setWorkspaces(data.workspaces || []);

      if ((data.workspaces || []).length === 0) {
        setError('No AWS IoT TwinMaker workspaces found in this region.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to AWS. Check your credentials and region.');
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch workspace details and connect
  const handleConnectWorkspace = async (workspace: AWSWorkspace) => {
    setConnecting(true);
    setError('');
    try {
      const response = await fetch(`${API}/aws/workspace-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspaceId: workspace.workspaceId,
          region: awsRegion,
          accessKey: awsAccessKey,
          secretKey: awsSecretKey,
        }),
      });

      const details = await response.json();

      if (!response.ok) {
        throw new Error(details.message || 'Failed to fetch workspace details');
      }
      
      // Create twin object from AWS workspace data
      const newTwin = saveTwin({
        name: workspace.workspaceName,
        company: workspace.workspaceName,
        factoryType: 'aws-connected',
        machines: [],
        sensors: [],
        workspaceId: workspace.workspaceId,
        twinMakerWsId: workspace.workspaceId,
        factoryId: workspace.workspaceId,
        machineCount: details.sceneCount || 0,
        sensorCount: details.entityCount || 0,
        isAWSConnected: true,
        awsRegion: awsRegion,
        awsLastSync: new Date().toISOString(),
      });

      setConnectedTwin({
        workspaceId: workspace.workspaceId,
        twinMakerWsId: workspace.workspaceId,
        company: workspace.workspaceName,
        factoryType: 'aws-connected',
        machineCount: details.sceneCount || 0,
        sensorCount: details.entityCount || 0,
        sceneCount: details.sceneCount || 0,
        status: 'Connected',
      });

      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/app', {
          state: { twinId: newTwin.id, connectedAwsTwin: true }
        });
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to connect to workspace');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="connect-twin-page">
      <div className="connect-container">
        {/* Header */}
        <div className="connect-header">
          <button className="back-btn" onClick={() => navigate('/')}>← Back</button>
          <h1>🔗 Connect Your AWS Twin</h1>
          <p>Connect your existing AWS IoT TwinMaker workspace to view insights</p>
        </div>

        {/* Success State */}
        {connectedTwin && (
          <div className="success-message">
            <div className="success-icon">✨</div>
            <h2>Twin Connected Successfully!</h2>
            <div className="twin-info">
              <div><strong>Company:</strong> {connectedTwin.company}</div>
              <div><strong>Workspace ID:</strong> {connectedTwin.workspaceId}</div>
              <div><strong>Entities:</strong> {connectedTwin.sensorCount}</div>
              <div><strong>Scenes:</strong> {connectedTwin.sceneCount}</div>
            </div>
            <p style={{marginTop: 24, color: '#9ca3af'}}>Redirecting to dashboard...</p>
          </div>
        )}

        {/* Auth Form */}
        {!connectedTwin && !showAuthForm && (
          <div className="auth-prompt">
            <div className="prompt-icon">🔑</div>
            <h2>AWS Credentials Required</h2>
            <p>To connect your AWS IoT TwinMaker workspace, please provide your AWS credentials.</p>
            <button className="btn-primary" onClick={() => setShowAuthForm(true)}>
              Enter AWS Credentials →
            </button>
          </div>
        )}

        {/* Credentials Form */}
        {!connectedTwin && showAuthForm && (
          <div className="auth-form">
            <h2>AWS Credentials</h2>
            <div className="form-fields">
              <div className="form-field">
                <label>AWS Region</label>
                <select
                  value={awsRegion}
                  onChange={e => setAwsRegion(e.target.value)}
                  className="input"
                >
                  <option value="us-east-1">us-east-1</option>
                  <option value="us-west-2">us-west-2</option>
                  <option value="eu-west-1">eu-west-1</option>
                  <option value="ap-southeast-1">ap-southeast-1</option>
                </select>
              </div>

              <div className="form-field">
                <label>AWS Access Key ID</label>
                <input
                  type="password"
                  placeholder="AKIA..."
                  value={awsAccessKey}
                  onChange={e => setAwsAccessKey(e.target.value)}
                  className="input"
                />
              </div>

              <div className="form-field">
                <label>AWS Secret Access Key</label>
                <input
                  type="password"
                  placeholder="••••••••••••••••"
                  value={awsSecretKey}
                  onChange={e => setAwsSecretKey(e.target.value)}
                  className="input"
                />
              </div>

              {error && <div className="error-msg">⚠️ {error}</div>}

              <button
                className="btn-primary btn-full"
                onClick={fetchWorkspaces}
                disabled={!awsAccessKey || !awsSecretKey || loading}
              >
                {loading ? '⏳ Searching...' : 'Search Workspaces →'}
              </button>

              <p style={{fontSize: 12, color: '#7a8899', marginTop: 12}}>
                🔒 Your credentials are not stored and are only used to fetch your workspaces.
              </p>
            </div>
          </div>
        )}

        {/* Workspaces List */}
        {!connectedTwin && workspaces.length > 0 && (
          <div className="workspaces-section">
            <h2>Available Workspaces ({workspaces.length})</h2>
            <div className="workspaces-list">
              {workspaces.map((workspace, idx) => (
                <div
                  key={idx}
                  className={`workspace-card ${selectedWorkspace?.workspaceId === workspace.workspaceId ? 'selected' : ''}`}
                  onClick={() => setSelectedWorkspace(workspace)}
                >
                  <div className="workspace-header">
                    <div className="workspace-name">
                      <h3>{workspace.workspaceName}</h3>
                      <p className="workspace-id">{workspace.workspaceId}</p>
                    </div>
                    <div className="workspace-status">
                      <span className="badge-green">● Active</span>
                    </div>
                  </div>
                  
                  <div className="workspace-details">
                    <div><strong>Created:</strong> {new Date(workspace.creationDateTime).toLocaleDateString()}</div>
                    <div><strong>Region:</strong> {workspace.region || awsRegion}</div>
                    <div><strong>S3 Bucket:</strong> <code>{workspace.s3Location}</code></div>
                  </div>

                  {selectedWorkspace?.workspaceId === workspace.workspaceId && (
                    <button
                      className="btn-primary btn-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConnectWorkspace(workspace);
                      }}
                      disabled={connecting}
                    >
                      {connecting ? '⏳ Connecting...' : '🔗 Connect This Workspace →'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!connectedTwin && !loading && !showAuthForm && workspaces.length === 0 && !error && (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h2>No Workspaces Found</h2>
            <p>Enter your AWS credentials to discover available IoT TwinMaker workspaces</p>
          </div>
        )}

        {/* Features Section */}
        <section className="connect-features">
          <h2>What You Can Do With Connected Twins</h2>
          <div className="features-grid">
            <div className="feature">
              <div className="icon">📊</div>
              <h3>Real-time Insights</h3>
              <p>View live telemetry and sensor data from your AWS workspace</p>
            </div>
            <div className="feature">
              <div className="icon">🔍</div>
              <h3>Full Visibility</h3>
              <p>See all scenes, entities, and models from your TwinMaker workspace</p>
            </div>
            <div className="feature">
              <div className="icon">🤖</div>
              <h3>AI Predictions</h3>
              <p>Get predictive maintenance insights powered by machine learning</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ConnectTwin;
